import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useNavigationType, Link } from 'react-router-dom';
import api from '../services/api';
import {
  getOrdersNeedingInvoice,
  bulkArchiveNoInvoice,
  bulkArchiveAllNoInvoice,
  OrderNeedingInvoice,
} from '../services/quickbooksService';
import InvoiceCreationModal from '../components/InvoiceCreationModal';
import { useDebounce } from '../hooks/useDebounce';
import { INVOICE_NEED_DATE_WARNING_DAYS } from '../constants/invoiceThresholds';
import './Invoices.css';

interface Invoice {
  id: number;
  invoice_number: string;
  order_number: string;
  customer_name: string;
  customer_id: number | null;
  status: string;
  subtotal: string;
  shipping_cost: string;
  tax: string;
  total: string;
  paid_amount: string;
  balance_due: string;
  due_date: string | null;
  // Customer NEED date (order.delivery_date) — distinct from due_date, which is
  // this date + net-terms offset. Drives the Action Needed / Future split.
  delivery_date: string | null;
  is_overdue: boolean;
  created_at: string;
  sent_at: string | null;
  paid_at: string | null;
  needs_qb_sync: boolean;
  // Post-approve follow-up state (Helcim link / customer email / QB push),
  // computed server-side. null for statuses the sweep can't act on.
  follow_up_state?: string | null;
  qb_invoice_id: string | null;
  last_synced_at: string | null;
  is_deleted?: boolean;
  deleted_at?: string | null;
  deleted_by_name?: string | null;
  deletion_reason?: string;
}

interface TabCounts {
  needs_review_action: number;
  needs_review_future: number;
  open: number;
  overdue: number;
  paid: number;
  all: number;
  deleted: number;
}

type TabType = 'needs_review_action' | 'needs_review_future' | 'open' | 'overdue' | 'paid' | 'all' | 'deleted';

const TAB_CONFIG: { id: TabType; label: string }[] = [
  { id: 'needs_review_action', label: 'Action Needed' },
  { id: 'needs_review_future', label: 'Future Orders' },
  { id: 'open', label: 'Open' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'paid', label: 'Paid' },
  { id: 'all', label: 'All' },
  { id: 'deleted', label: 'Deleted' },
];

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft: { bg: '#f3f4f6', text: '#6b7280' },
  pending_review: { bg: '#fef3c7', text: '#92400e' },
  approved: { bg: '#dbeafe', text: '#1e40af' },
  sent: { bg: '#dbeafe', text: '#1e40af' },
  ach_pending: { bg: '#fef3c7', text: '#92400e' },
  partial: { bg: '#fce7f3', text: '#9d174d' },
  paid: { bg: '#d1fae5', text: '#065f46' },
  void: { bg: '#fee2e2', text: '#991b1b' },
  overdue: { bg: '#fee2e2', text: '#991b1b' },
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  pending_review: 'Needs Review',
  approved: 'Approved',
  sent: 'Sent',
  ach_pending: 'ACH Processing',
  partial: 'Partial',
  paid: 'Paid',
  void: 'Void',
};

const INVOICES_SCROLL_KEY = 'invoicesListScrollY';

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const navType = useNavigationType();
  // Capture the navigation type AT MOUNT: the URL-sync effect below calls
  // setSearchParams(replace), which would flip useNavigationType() to 'REPLACE' before
  // the scroll-restore reads it and mask that we arrived via back/forward (POP).
  const arrivedViaPop = useRef(navType === 'POP').current;
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const tabParam = searchParams.get('tab');
    // Legacy deep links used ?tab=needs_review (now split in two) — land them
    // on Action Needed so nothing silently disappears.
    if (tabParam === 'needs_review' || !tabParam) return 'needs_review_action';
    return tabParam as TabType;
  });
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabCounts, setTabCounts] = useState<TabCounts>({
    needs_review_action: 0,
    needs_review_future: 0,
    open: 0,
    overdue: 0,
    paid: 0,
    all: 0,
    deleted: 0,
  });
  // Overdue-only narrowing — only meaningful on the Action Needed tab. Server
  // does the filtering (overdue_only=true); resets whenever the tab changes.
  // Filter/search/page state seeded from the URL so refresh + back-navigation restore it.
  const [overdueOnly, setOverdueOnly] = useState(() => searchParams.get('overdue') === '1');
  const [currentPage, setCurrentPage] = useState(() => Number(searchParams.get('page')) || 1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;

  // Search input is debounced 300ms before firing the network call. Pressing
  // Enter bypasses the debounce by calling setSearchTerm immediately.
  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') || '');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');

  // Restore-action state for the Deleted tab.
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  // Orders needing invoices state
  const [ordersNeedingInvoice, setOrdersNeedingInvoice] = useState<OrderNeedingInvoice[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [selectedOrderForInvoice, setSelectedOrderForInvoice] = useState<OrderNeedingInvoice | null>(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<number>>(new Set());
  const [archiving, setArchiving] = useState(false);
  const [archivingAll, setArchivingAll] = useState(false);
  const [ordersMessage, setOrdersMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load invoices
  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params: Record<string, string> = {
        page: String(currentPage),
        per_page: String(pageSize),
      };

      if (activeTab !== 'all') {
        params.filter = activeTab;
      }
      // Overdue-only is honoured server-side and only on the Action Needed tab,
      // so pagination stays correct. Backend ignores it for other filters.
      if (activeTab === 'needs_review_action' && overdueOnly) {
        params.overdue_only = 'true';
      }
      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await api.get('/invoices/', { params });
      setInvoices(response.data.results || []);
      setTotalCount(response.data.total_count || 0);
    } catch (err: any) {
      console.error('Error loading invoices:', err);
      setError(err.response?.data?.error || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage, searchTerm, overdueOnly]);

  // Load tab counts
  const loadTabCounts = useCallback(async () => {
    try {
      // Load counts for each tab (same per_page:1 mechanism as before).
      const [action, future, open, overdue, paid, all, deleted] = await Promise.all([
        api.get('/invoices/', { params: { filter: 'needs_review_action', per_page: 1 } }),
        api.get('/invoices/', { params: { filter: 'needs_review_future', per_page: 1 } }),
        api.get('/invoices/', { params: { filter: 'open', per_page: 1 } }),
        api.get('/invoices/', { params: { filter: 'overdue', per_page: 1 } }),
        api.get('/invoices/', { params: { filter: 'paid', per_page: 1 } }),
        api.get('/invoices/', { params: { per_page: 1 } }),
        api.get('/invoices/', { params: { filter: 'deleted', per_page: 1 } }),
      ]);

      setTabCounts({
        needs_review_action: action.data.total_count || 0,
        needs_review_future: future.data.total_count || 0,
        open: open.data.total_count || 0,
        overdue: overdue.data.total_count || 0,
        paid: paid.data.total_count || 0,
        all: all.data.total_count || 0,
        deleted: deleted.data.total_count || 0,
      });
    } catch (err) {
      console.error('Error loading tab counts:', err);
    }
  }, []);

  // Load orders needing invoices
  const loadOrdersNeedingInvoice = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const result = await getOrdersNeedingInvoice();
      setOrdersNeedingInvoice(result.orders);
    } catch (err) {
      console.error('Failed to load orders needing invoice:', err);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  const handleToggleOrderSelection = (orderId: number) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const handleSelectAllOrders = () => {
    if (selectedOrderIds.size === ordersNeedingInvoice.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(ordersNeedingInvoice.map(o => o.id)));
    }
  };

  const handleBulkArchive = async () => {
    if (selectedOrderIds.size === 0) return;
    const count = selectedOrderIds.size;
    if (!window.confirm(`Archive ${count} order${count > 1 ? 's' : ''}? They won't receive invoices.`)) return;
    setArchiving(true);
    setOrdersMessage(null);
    try {
      const result = await bulkArchiveNoInvoice(Array.from(selectedOrderIds));
      setOrdersMessage({ type: 'success', text: result.message });
      setSelectedOrderIds(new Set());
      loadOrdersNeedingInvoice();
    } catch (err) {
      console.error('Failed to archive orders:', err);
      setOrdersMessage({ type: 'error', text: 'Failed to archive orders' });
    } finally {
      setArchiving(false);
    }
  };

  const handleBulkArchiveAll = async () => {
    const totalOrders = ordersNeedingInvoice.length;
    if (totalOrders === 0) return;
    if (!window.confirm(
      `Archive ALL ${totalOrders} orders without invoices?\n\nThis covers all pages. These are old imported orders that were already handled outside the system.`
    )) return;
    setArchivingAll(true);
    setOrdersMessage(null);
    try {
      const result = await bulkArchiveAllNoInvoice();
      setOrdersMessage({ type: 'success', text: result.message });
      setSelectedOrderIds(new Set());
      loadOrdersNeedingInvoice();
    } catch (err) {
      console.error('Failed to archive all orders:', err);
      setOrdersMessage({ type: 'error', text: 'Failed to archive all orders' });
    } finally {
      setArchivingAll(false);
    }
  };

  const handleInvoiceCreated = (invoiceNumber: string, sent: boolean) => {
    setSelectedOrderForInvoice(null);
    setOrdersMessage({
      type: 'success',
      text: sent
        ? `Invoice ${invoiceNumber} created and sent to customer!`
        : `Invoice ${invoiceNumber} saved as draft.`,
    });
    loadOrdersNeedingInvoice();
    loadInvoices();
    loadTabCounts();
  };

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    loadTabCounts();
    loadOrdersNeedingInvoice();
  }, [loadTabCounts, loadOrdersNeedingInvoice]);

  // Propagate debounced search to the network-layer searchTerm. Page resets to 1 when
  // the user CHANGES the search — but NOT on the initial mount, so a page number restored
  // from the URL (back-navigation / refresh) survives.
  const searchMountRef = useRef(false);
  useEffect(() => {
    setSearchTerm(debouncedSearch);
    if (searchMountRef.current) {
      setCurrentPage(1);
    } else {
      searchMountRef.current = true;
    }
  }, [debouncedSearch]);

  // Mirror ALL list filters into the URL so refresh + back-navigation restore them.
  // Keyed on the DEBOUNCED search (searchTerm), so the URL updates once per pause, not
  // per keystroke. Functional updater + replace:true: preserves any other params and
  // never stacks a history entry (back goes to the previous page, not each filter change).
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', activeTab);
      if (currentPage > 1) next.set('page', String(currentPage)); else next.delete('page');
      if (searchTerm) next.set('q', searchTerm); else next.delete('q');
      if (overdueOnly) next.set('overdue', '1'); else next.delete('overdue');
      return next;
    }, { replace: true });
  }, [activeTab, currentPage, searchTerm, overdueOnly, setSearchParams]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }
  };

  // Tab change: the URL is written by the sync effect above (no explicit setSearchParams
  // here, so it doesn't push a separate history entry).
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setOverdueOnly(false); // overdue-only is Action-Needed-only; reset on switch
  };

  // Navigate into an invoice, saving the list's scroll position first so we can restore
  // it when the user comes back. Use for every row/button that opens /sales/invoices/:id.
  const goToInvoice = useCallback((id: number) => {
    try { sessionStorage.setItem(INVOICES_SCROLL_KEY, String(window.scrollY)); } catch { /* ignore */ }
    navigate(`/sales/invoices/${id}`);
  }, [navigate]);

  // Restore the saved scroll on BACK-navigation to this list, AFTER the rows render
  // (loading→false = fetch resolved + setInvoices committed, so the document has height).
  // rAF runs after the app's <ScrollToTop> mount rAF, so our restore wins. Gated on the
  // frozen-at-mount POP value (arrivedViaPop) so the URL-sync replace doesn't mask it.
  const didRestoreScroll = useRef(false);
  useEffect(() => {
    if (loading || didRestoreScroll.current) return;
    didRestoreScroll.current = true;
    const saved = sessionStorage.getItem(INVOICES_SCROLL_KEY);
    sessionStorage.removeItem(INVOICES_SCROLL_KEY);
    if (arrivedViaPop && saved) {
      requestAnimationFrame(() => window.scrollTo(0, parseInt(saved, 10)));
    }
  }, [loading, arrivedViaPop]);

  const handleRestore = async (invoiceId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(
      'Restore this invoice? It will become visible to the customer again. ' +
      'No QuickBooks or Helcim re-sync is triggered.'
    )) {
      return;
    }
    setRestoreError(null);
    setRestoringId(invoiceId);
    try {
      await api.post(`/invoices/${invoiceId}/restore/`);
      // Reload list + counts to reflect the restored invoice moving
      // back into its appropriate tab.
      loadInvoices();
      loadTabCounts();
    } catch (err: any) {
      setRestoreError(err?.response?.data?.error || 'Failed to restore invoice');
    } finally {
      setRestoringId(null);
    }
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num || 0);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusDisplay = (invoice: Invoice) => {
    // Check if overdue takes priority (but not for ACH pending — payment is in transit)
    if (invoice.is_overdue && invoice.status !== 'paid' && invoice.status !== 'ach_pending') {
      return { label: 'Overdue', ...STATUS_COLORS.overdue };
    }
    const colors = STATUS_COLORS[invoice.status] || STATUS_COLORS.draft;
    const label = STATUS_LABELS[invoice.status] || invoice.status;
    return { label, ...colors };
  };

  // Follow-up badge — surfaces an approve whose Helcim link / customer email /
  // QB push did not finish, in the list staff already use. 'complete' shows
  // nothing: a badge on every healthy invoice is noise nobody reads.
  //
  // 'stalled' is the one that matters — it means the safety sweep re-queued
  // the work three times and it never reported back, which is what a worker
  // being OOM-killed mid-task looks like from the outside. Those need a person
  // (Sync Helcim, then Send to Customer).
  const getFollowUpBadge = (state?: string | null) => {
    switch (state) {
      case 'stalled':
        return { label: 'Follow-up stalled', bg: '#fee2e2', text: '#991b1b',
                 title: 'Payment link / customer email never completed — use Sync Helcim, then Send to Customer' };
      case 'failed':
        return { label: 'Follow-up failed', bg: '#fee2e2', text: '#991b1b',
                 title: 'Follow-up work never started' };
      case 'partial':
        return { label: 'Follow-up partial', bg: '#fef3c7', text: '#92400e',
                 title: 'Approved, but a follow-up step did not finish — open the invoice for detail' };
      case 'pending':
        return { label: 'Follow-up pending', bg: '#e0e7ff', text: '#3730a3',
                 title: 'Payment link / customer email still in progress' };
      default:
        return null;
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // Local midnight — compare against the order NEED date to flag past-due need
  // dates on the Action Needed tab. pending_review invoices are never
  // is_overdue (that property excludes pending_review), so the need date is the
  // only past-due signal here.
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const isNeedDatePast = (invoice: Invoice) =>
    !!invoice.delivery_date && new Date(invoice.delivery_date) < todayStart;
  // Reuse the file's existing 'overdue' row/card styling — do not invent a color.
  const rowIsWarning = (invoice: Invoice) =>
    invoice.is_overdue ||
    (activeTab === 'needs_review_action' && isNeedDatePast(invoice));

  return (
    <div className="invoices-page">
      <div className="invoices-header">
        <div>
          <h1>Invoices</h1>
          <p className="invoices-subtitle">Manage and track customer invoices</p>
        </div>
      </div>

      {/* Orders Needing Invoices */}
      {ordersNeedingInvoice.length > 0 && (
        <div className="orders-needing-invoices">
          <div className="oni-header">
            <h2>
              Orders Needing Invoices
              <span className="oni-count">({ordersNeedingInvoice.length})</span>
            </h2>
            <div className="oni-actions">
              {selectedOrderIds.size > 0 && (
                <button className="btn btn-danger-sm" onClick={handleBulkArchive} disabled={archiving || archivingAll}>
                  {archiving ? 'Archiving...' : `Archive ${selectedOrderIds.size} Selected`}
                </button>
              )}
              {ordersNeedingInvoice.length > 0 && (
                <button className="btn btn-danger-outline-sm" onClick={handleBulkArchiveAll} disabled={archiving || archivingAll}>
                  {archivingAll ? 'Archiving All...' : `Archive All (${ordersNeedingInvoice.length})`}
                </button>
              )}
              <button className="btn btn-secondary-sm" onClick={loadOrdersNeedingInvoice} disabled={ordersLoading}>
                {ordersLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>

          {ordersMessage && (
            <div className={`message ${ordersMessage.type}`}>
              {ordersMessage.text}
            </div>
          )}

          {ordersLoading ? (
            <div className="invoices-loading">Loading orders...</div>
          ) : (
            <div className="invoices-table-container">
              <table className="invoices-table oni-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedOrderIds.size === ordersNeedingInvoice.length && ordersNeedingInvoice.length > 0}
                        onChange={handleSelectAllOrders}
                      />
                    </th>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Order Date</th>
                    <th>Delivery Date</th>
                    <th className="text-right">Total</th>
                    <th>Shipping</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {ordersNeedingInvoice.map(order => (
                    <tr key={order.id}>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedOrderIds.has(order.id)}
                          onChange={() => handleToggleOrderSelection(order.id)}
                        />
                      </td>
                      <td><strong>#{order.order_number}</strong></td>
                      <td>
                        <span>{order.customer_name}</span>
                        {order.customer_email && (
                          <span className="customer-email-sm">{order.customer_email}</span>
                        )}
                      </td>
                      <td>{formatDate(order.order_date)}</td>
                      <td>{formatDate(order.delivery_date)}</td>
                      <td className="text-right">{formatCurrency(order.total)}</td>
                      <td>{order.shipping_method || 'Not set'}</td>
                      <td>
                        <span className="status-badge" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
                          {order.status}
                        </span>
                      </td>
                      <td>
                        <button className="btn-view" onClick={() => setSelectedOrderForInvoice(order)}>
                          Create Invoice
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Invoice Creation Modal */}
      {selectedOrderForInvoice && (
        <InvoiceCreationModal
          order={selectedOrderForInvoice}
          onClose={() => setSelectedOrderForInvoice(null)}
          onSuccess={handleInvoiceCreated}
        />
      )}

      {/* Tabs */}
      <div className="invoices-tabs">
        {TAB_CONFIG.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
            <span
              className={`tab-badge ${
                (tab.id === 'overdue' && tabCounts.overdue > 0) ||
                (tab.id === 'needs_review_action' && tabCounts.needs_review_action > 0)
                  ? 'badge-warning'
                  : ''
              }`}
            >
              {tabCounts[tab.id]}
            </span>
          </button>
        ))}
      </div>

      {/* Tab explainer — the 14 comes from the shared threshold, never inline */}
      {activeTab === 'needs_review_action' && (
        <p className="invoices-tab-note" style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
          Action Needed: Need date within {INVOICE_NEED_DATE_WARNING_DAYS} days or passed.
          Customer cannot see or pay these until approved.
        </p>
      )}
      {activeTab === 'needs_review_future' && (
        <p className="invoices-tab-note" style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
          Future Orders: Need date more than {INVOICE_NEED_DATE_WARNING_DAYS} days out.
          These will move to Action Needed automatically.
        </p>
      )}

      {/* Overdue-only toggle — Action Needed tab only, filtered server-side */}
      {activeTab === 'needs_review_action' && (
        <label
          className="invoices-overdue-toggle"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#6b7280', fontSize: '0.85rem', margin: '0 0 0.75rem', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(e) => { setOverdueOnly(e.target.checked); setCurrentPage(1); }}
          />
          Overdue only (need date already passed)
        </label>
      )}

      {/* Search */}
      <div className="invoices-search">
        <input
          type="text"
          placeholder="Search by invoice number, customer, or order #..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          className="search-input"
        />
      </div>

      {restoreError && (
        <div className="invoices-error">{restoreError}</div>
      )}

      {/* Error message */}
      {error && (
        <div className="invoices-error">
          {error}
          <button onClick={loadInvoices}>Retry</button>
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="invoices-loading">Loading invoices...</div>
      ) : invoices.length === 0 ? (
        <div className="invoices-empty">
          <p>No invoices found</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="invoices-table-container">
            <table className="invoices-table">
              <thead>
                {activeTab === 'deleted' ? (
                  <tr>
                    <th>Invoice #</th>
                    <th>Customer</th>
                    <th>Order #</th>
                    <th className="text-right">Total</th>
                    <th>Deleted At</th>
                    <th>Deleted By</th>
                    <th>Reason</th>
                    <th>Actions</th>
                  </tr>
                ) : (
                  <tr>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Order #</th>
                    <th>Need Date</th>
                    <th>Due Date</th>
                    <th className="text-right">Total</th>
                    <th className="text-right">Balance Due</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  const statusDisplay = getStatusDisplay(invoice);
                  const hasBalance = parseFloat(invoice.balance_due) > 0;

                  if (activeTab === 'deleted') {
                    return (
                      <tr
                        key={invoice.id}
                        className="invoice-row invoice-row-deleted"
                        onClick={() => goToInvoice(invoice.id)}
                      >
                        <td className="invoice-number">
                          <strong>{invoice.invoice_number || `INV-${invoice.id}`}</strong>
                        </td>
                        <td>{invoice.customer_name}</td>
                        <td>
                          <Link
                            to={`/orders?search=${invoice.order_number}`}
                            onClick={(e) => e.stopPropagation()}
                            className="order-link"
                          >
                            {invoice.order_number}
                          </Link>
                        </td>
                        <td className="text-right">{formatCurrency(invoice.total)}</td>
                        <td>{formatDate(invoice.deleted_at || null)}</td>
                        <td>{invoice.deleted_by_name || '—'}</td>
                        <td title={invoice.deletion_reason || ''}>
                          {invoice.deletion_reason || '—'}
                        </td>
                        <td>
                          <button
                            className="btn-view"
                            onClick={(e) => {
                              e.stopPropagation();
                              goToInvoice(invoice.id);
                            }}
                          >
                            View
                          </button>
                          <button
                            className="btn-view"
                            style={{ marginLeft: 6 }}
                            disabled={restoringId === invoice.id}
                            onClick={(e) => handleRestore(invoice.id, e)}
                          >
                            {restoringId === invoice.id ? 'Restoring…' : 'Restore'}
                          </button>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr
                      key={invoice.id}
                      className={`invoice-row ${rowIsWarning(invoice) ? 'overdue' : ''}`}
                      onClick={() => goToInvoice(invoice.id)}
                    >
                      <td className="invoice-number">
                        <strong>{invoice.invoice_number || `INV-${invoice.id}`}</strong>
                      </td>
                      <td>{formatDate(invoice.created_at)}</td>
                      <td>{invoice.customer_name}</td>
                      <td>
                        <Link
                          to={`/orders?search=${invoice.order_number}`}
                          onClick={(e) => e.stopPropagation()}
                          className="order-link"
                        >
                          {invoice.order_number}
                        </Link>
                      </td>
                      <td>{formatDate(invoice.delivery_date)}</td>
                      <td>{formatDate(invoice.due_date)}</td>
                      <td className="text-right">{formatCurrency(invoice.total)}</td>
                      <td className={`text-right ${hasBalance ? 'balance-due' : 'balance-paid'}`}>
                        {formatCurrency(invoice.balance_due)}
                      </td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            backgroundColor: statusDisplay.bg,
                            color: statusDisplay.text,
                          }}
                        >
                          {statusDisplay.label}
                        </span>
                        {(() => {
                          const fu = getFollowUpBadge(invoice.follow_up_state);
                          return fu ? (
                            <span
                              className="status-badge"
                              style={{
                                backgroundColor: fu.bg,
                                color: fu.text,
                                marginLeft: 6,
                                fontSize: '0.7rem',
                              }}
                              title={fu.title}
                            >
                              {fu.label}
                            </span>
                          ) : null;
                        })()}
                        {invoice.needs_qb_sync && (
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              marginLeft: 6,
                              fontSize: '0.7rem',
                            }}
                            title="Invoice not synced to QuickBooks"
                          >
                            QB Pending
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn-view"
                          onClick={(e) => {
                            e.stopPropagation();
                            goToInvoice(invoice.id);
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="invoices-mobile">
            {invoices.map((invoice) => {
              const statusDisplay = getStatusDisplay(invoice);
              const hasBalance = parseFloat(invoice.balance_due) > 0;

              return (
                <div
                  key={invoice.id}
                  className={`invoice-card ${rowIsWarning(invoice) ? 'overdue' : ''}`}
                  onClick={() => goToInvoice(invoice.id)}
                >
                  <div className="invoice-card-header">
                    <div>
                      <strong>{invoice.invoice_number || `INV-${invoice.id}`}</strong>
                      <span className="invoice-date">{formatDate(invoice.created_at)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span
                        className="status-badge"
                        style={{
                          backgroundColor: statusDisplay.bg,
                          color: statusDisplay.text,
                        }}
                      >
                        {statusDisplay.label}
                      </span>
                      {(() => {
                        const fu = getFollowUpBadge(invoice.follow_up_state);
                        return fu ? (
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor: fu.bg,
                              color: fu.text,
                              fontSize: '0.65rem',
                            }}
                            title={fu.title}
                          >
                            {fu.label}
                          </span>
                        ) : null;
                      })()}
                      {invoice.needs_qb_sync && (
                        <span
                          className="status-badge"
                          style={{
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                            fontSize: '0.65rem',
                          }}
                        >
                          QB Pending
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="invoice-card-body">
                    <div className="invoice-card-row">
                      <span className="label">Customer</span>
                      <span>{invoice.customer_name}</span>
                    </div>
                    <div className="invoice-card-row">
                      <span className="label">Order</span>
                      <span>{invoice.order_number}</span>
                    </div>
                    <div className="invoice-card-row">
                      <span className="label">Need Date</span>
                      <span>{formatDate(invoice.delivery_date)}</span>
                    </div>
                    <div className="invoice-card-row">
                      <span className="label">Due Date</span>
                      <span>{formatDate(invoice.due_date)}</span>
                    </div>
                    <div className="invoice-card-row">
                      <span className="label">Total</span>
                      <span>{formatCurrency(invoice.total)}</span>
                    </div>
                    <div className="invoice-card-row">
                      <span className="label">Balance Due</span>
                      <span className={hasBalance ? 'balance-due' : 'balance-paid'}>
                        {formatCurrency(invoice.balance_due)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="invoices-pagination">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Invoices;
